import React, { useState } from "react";
import FlexBox from "design-system/src/Components/FlexBox/FlexBox";
import Typography from "design-system/src/Foundation/Typography/Typography";
import { useTranslation } from "@src/hooks/useTranslation";
import Button from "design-system/src/Foundation/Button/Button";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { _AccountOrderButtonWrapper } from "../GameParticipationModal.styled";
import Input from "design-system/src/Form/Input/Input";
import CheckBox from "design-system/src/Form/CheckBox/CheckBox";
import { useAddGameParticipantMutation } from "@src/services/rollingStartApi";
import { useConfiguration } from "@src/hooks/query/useConfiguration";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import htmlParse from "html-react-parser";
import type { IParticipationForm } from "@src/interface/GameParticipant";

export type { IParticipationForm };

export interface IParticipationFormProps {
  onSuccess?: () => void;
  data: Pick<IParticipationForm, "score" | "pseudo" | "pictoID">;
}

const ParticipationForm = (props: IParticipationFormProps) => {
  const { onSuccess, data } = props;
  const { t } = useTranslation(["Pacman"]);
  const [loading, setLoading] = useState(false);
  const [addGameParticipant] = useAddGameParticipantMutation();
  const { getConfiguration: c } = useConfiguration();
  const { app } = useBackboneContext();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<IParticipationForm>({
    defaultValues: {
      ...data,
      email: app?.getCustomer().get("email") || "",
    },
  });

  const onSubmit: SubmitHandler<IParticipationForm> = async (formData) => {
    try {
      setLoading(true);
      const res = await addGameParticipant(formData);
      if (res) {
        onSuccess?.();
      }
    } catch (error) {
      // TODO: handle error
    } finally {
      setLoading(false);
    }
  };

  const _PACMAN_FORM_CGV_ = c(
    "_PACMAN_FORM_CGV_",
    "<p>En cochant la case « Je m’inscris au jeu concours », les participants acceptent les termes et conditions de la promotion tels que décrits dans ses Termes et  Conditions. Les participants acceptent les termes et conditions de la promotion tels que décrits dans les présentes Conditions générales et acceptent  expressément de se conformer à toute décision interprétative prises par Sushi Shop®.</p>"
  );
  const _PACMAN_FORM_SUBSCRIBE_NEWSLETTER_ = c(
    "_PACMAN_FORM_SUBSCRIBE_NEWSLETTER_",
    "En cochant la case « Je m’inscris aux communications » Sushi Shop s'engage à respecter la vie privée de chacun de ses clients. Les données personnelles recueillies sur cette page sont destinées à l'entité Sushi Shop choisie et concernée (France, Royaume-Uni, Suisse, Belgique, Espagne) pour l'envoi de communications sur les offres, actualités et événements Sushi Shop dans le cadre de la gestion de sa relation client et de sa relation commerciale. Pour plus d'informations sur le traitement de vos données personnelles, veuillez consulter notre Déclaration de confidentialité. Vous pouvez également nous demander de ne pas vous envoyer de communications personnalisées sur nos produits et services. Vous pouvez exercer ce droit à tout moment, en nous envoyant une notification en vous référant à notre section Contact dans notre Déclaration de confidentialité ou en utilisant le lien \"se désinscrire\" intégré dans chaque e-mail que Sushi Shop vous enverra."
  );

  return (
    <FlexBox
      as={"form"}
      onSubmit={handleSubmit(onSubmit)}
      direction="column"
      gap={24}
    >
      <FlexBox direction="column" gap={24} style={{ width: "100%" }}>
        <Controller
          name={"email"}
          control={control}
          rules={{ required: true }}
          render={({ field: controllerField }) => (
            <Input
              icon={"message"}
              label={t("Renseignez votre email", "Pacman") + "*"}
              value={controllerField.value}
              onChange={controllerField.onChange}
            />
          )}
        />

        <Controller
          name="optinCgv"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <CheckBox align="top" linkInLabel={true} {...field}>
              {({ ...typographyProps }) => (
                <Typography {...typographyProps} variant="button2">
                  {t("Je m’inscris au jeu concours", "Pacman")} <sup>*</sup>
                </Typography>
              )}
            </CheckBox>
          )}
        />

        <Controller
          name="newsletter"
          control={control}
          rules={{
            required: false,
          }}
          render={({ field }) => (
            <CheckBox align="top" linkInLabel={true} {...field}>
              {({ ...typographyProps }) => (
                <Typography {...typographyProps} variant="button2">
                  {t("Je m’inscris aux communication", "Pacman")} <sup>**</sup>
                </Typography>
              )}
            </CheckBox>
          )}
        />

        <Typography variant="ui4" color="greyText2">
          <span style={{ float: "left", paddingRight: 4 }}>* </span>{" "}
          {htmlParse(_PACMAN_FORM_CGV_ || "")}
        </Typography>
        <Typography variant="ui4" color="greyText2">
          <span style={{ float: "left", paddingRight: 4 }}>** </span>{" "}
          {htmlParse(_PACMAN_FORM_SUBSCRIBE_NEWSLETTER_ || "")}
        </Typography>
      </FlexBox>

      <_AccountOrderButtonWrapper>
        <Button
          style={{ position: "relative" }}
          type="submit"
          color={"primary"}
          onClick={handleSubmit}
          disabled={!isDirty || !isValid}
          loading={loading}
        >
          {t("Valider", "Pacman")}
        </Button>
      </_AccountOrderButtonWrapper>
    </FlexBox>
  );
};

export default ParticipationForm;
